import { DEFAULT_RAILING, DEFAULT_STAIR_CONFIG, IStairConfig, railingMaterialsOf, risersEnabledOf } from '@stair-craft/common/util';

// The one place that turns a saved `.json` into a stair config. Pure: no Angular DI, no
// notifications — a caller catches the failure and shows the message this module names for it.

/** Why a picked or dropped file did not become a stair config. */
export enum EStairConfigLoadFailure {
    NotJsonName = 'not-json-name',
    Unreadable = 'unreadable',
    BrokenJson = 'broken-json',
    NotAConfig = 'not-a-config',
}

/** The typed reason the loader throws, so every caller shows one message per reason. */
export class StairConfigLoadError extends Error {
    public readonly failure: EStairConfigLoadFailure;

    constructor(failure: EStairConfigLoadFailure) {
        super(`stair config file load failed: ${failure}`);
        this.name = 'StairConfigLoadError';
        this.failure = failure;
    }
}

// The four marks the configurator has always required of an imported file.
function isStairConfigShape(raw: unknown): raw is Partial<IStairConfig> {
    if (!raw || typeof raw !== 'object') {
        return false;
    }
    const record: Partial<IStairConfig> = raw;

    return (
        typeof record.type === 'string' &&
        typeof record.roomLength === 'number' &&
        typeof record.roomWidth === 'number' &&
        typeof record.floorHeight === 'number'
    );
}

/**
 * Parses the text of a saved `.json` into a config, filling what the file omits with the
 * defaults. Throws `StairConfigLoadError` with `BrokenJson` or `NotAConfig`.
 */
export function parseStairConfigText(text: string): IStairConfig {
    let raw: unknown;
    try {
        raw = JSON.parse(text);
    } catch {
        throw new StairConfigLoadError(EStairConfigLoadFailure.BrokenJson);
    }

    if (!isStairConfigShape(raw)) {
        throw new StairConfigLoadError(EStairConfigLoadFailure.NotAConfig);
    }

    const config: IStairConfig = {
        ...DEFAULT_STAIR_CONFIG,
        ...raw,
        railing: { ...DEFAULT_RAILING, ...raw.railing },
    };
    // A file saved before the railing had species of its own opens it in the treads' wood.
    return { ...config, ...railingMaterialsOf(raw, config.treadMaterialId), risersEnabled: risersEnabledOf(raw) };
}

/**
 * Reads a file the person picked or dropped. Rejects with `StairConfigLoadError`: a name that is
 * not `.json` is refused before the read, so the picker and the drop zone answer alike.
 */
export function readStairConfigFile(file: File): Promise<IStairConfig> {
    if (!file.name.toLowerCase().endsWith('.json')) {
        return Promise.reject(new StairConfigLoadError(EStairConfigLoadFailure.NotJsonName));
    }

    return new Promise<IStairConfig>((resolve: (config: IStairConfig) => void, reject: (reason: Error) => void): void => {
        const reader: FileReader = new FileReader();
        reader.onload = (): void => {
            const text: string = typeof reader.result === 'string' ? reader.result : '';
            try {
                resolve(parseStairConfigText(text));
            } catch (error: unknown) {
                reject(error instanceof Error ? error : new StairConfigLoadError(EStairConfigLoadFailure.BrokenJson));
            }
        };
        reader.onerror = (): void => {
            reject(new StairConfigLoadError(EStairConfigLoadFailure.Unreadable));
        };
        reader.readAsText(file);
    });
}

/** The message shown for a failed load — the same wording from the picker and the drop zone. */
export function stairConfigLoadErrorMessage(error: unknown): string {
    const failure: EStairConfigLoadFailure = error instanceof StairConfigLoadError ? error.failure : EStairConfigLoadFailure.Unreadable;

    switch (failure) {
        case EStairConfigLoadFailure.NotAConfig:
            return $localize`:@@loadErrorInvalidConfig:Файл не содержит корректной конфигурации лестницы.`;
        case EStairConfigLoadFailure.Unreadable:
            return $localize`:@@loadErrorReadFailed:Не удалось прочитать файл.`;
        default:
            return $localize`:@@loadErrorInvalidJson:Не удалось прочитать .json — проверьте формат файла.`;
    }
}
